import type { AttendanceStatus, EventType } from './event';

export interface Vehicle {
  id: number;
  name: string;
  /** Utasférőhelyek száma, a sofőrön kívül. */
  seats: number;
  licensePlate?: string | null;
  notes?: string | null;
  isActive: boolean;
}

export type SaveVehicleRequest = Omit<Vehicle, 'id'>;

export interface TravelStop {
  id: number;
  name: string;
  address?: string | null;
  /** Mikorra kell a megállóban lenni (helyi idő, időzóna nélkül). */
  meetTime?: string | null;
}

export interface TravelVehicle {
  id: number;
  vehicleId?: number | null;
  name: string;
  seats: number;
  driverName?: string | null;
  notes?: string | null;
  /** Egy szülő ajánlotta fel (nem a stáb vette fel). */
  isOffered?: boolean;
  /** A bejelentkezett felhasználó ajánlotta fel. */
  isMine?: boolean;
  /** A felajánló neve. */
  ownerName?: string | null;
  stops: TravelStop[];
}

export interface TravelPassenger {
  id: number;
  /** A játékos; null, ha plusz személy. */
  playerId?: number | null;
  name: string;
  role?: string | null;
  goesDirectly: boolean;
  travelVehicleId?: number | null;
  travelStopId?: number | null;
  notes?: string | null;
  /** A bejelentkezett felhasználó maga vagy a gyermeke. */
  isMine: boolean;
}

export interface TravelRosterPlayer {
  playerId: number;
  name: string;
  response?: AttendanceStatus | null;
  /** Hamis, ha korábban beosztották, de azóta kikerült a nevezésből. */
  inRoster?: boolean;
}

export interface EventTravel {
  eventId: number;
  eventTitle: string;
  eventType: EventType;
  eventStart: string;
  destination?: string | null;
  exists: boolean;
  canEdit: boolean;
  /** Jelezhet-e a bejelentkezett felhasználó (fuvar kell, helyszínre megy, autót ajánl). */
  canRespond?: boolean;
  /** A bejelentkezett felhasználó maga és a gyermekei, akik a keretben vannak. */
  myPlayers?: TravelRosterPlayer[];
  notes?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
  vehicles: TravelVehicle[];
  passengers: TravelPassenger[];
  /** Az esemény kerete a visszajelzésekkel (szerkesztőnek és a jelezni tudóknak). */
  roster: TravelRosterPlayer[];
  /** 'squad' = a mérkőzés nevezése a keret, 'ageGroup' = nincs nevezés, a teljes korosztály. */
  rosterSource?: 'squad' | 'ageGroup';
}

export interface SaveTravelStopInput {
  key: string;
  name: string;
  address?: string | null;
  meetTime?: string | null;
}

export interface SaveTravelVehicleInput {
  key: string;
  /** A már elmentett jármű azonosítója: így megmarad, melyik szülő ajánlotta fel. */
  existingId?: number | null;
  vehicleId?: number | null;
  name: string;
  seats: number;
  driverName?: string | null;
  notes?: string | null;
  stops: SaveTravelStopInput[];
}

export interface SaveTravelPassengerInput {
  playerId?: number | null;
  name?: string | null;
  role?: string | null;
  goesDirectly: boolean;
  vehicleKey?: string | null;
  stopKey?: string | null;
  notes?: string | null;
}

export interface SaveEventTravelRequest {
  notes?: string | null;
  /** A betöltéskori updatedAt: ha azóta más módosította, a mentés 409-et ad. */
  baseUpdatedAt?: string | null;
  checkConflicts?: boolean;
  vehicles: SaveTravelVehicleInput[];
  passengers: SaveTravelPassengerInput[];
}

export interface MyTravelAssignment {
  name: string;
  goesDirectly: boolean;
  /** Jelezte, hogy fuvar kell, de még nincs beosztva. */
  needsRide?: boolean;
  vehicleName?: string | null;
  stopName?: string | null;
  meetTime?: string | null;
}

export interface TravelOverview {
  eventId: number;
  eventTitle: string;
  eventType: EventType;
  eventStart: string;
  destination?: string | null;
  ageGroup: string;
  hasTravel: boolean;
  canEdit: boolean;
  vehicleCount: number;
  totalSeats: number;
  seatedCount: number;
  directCount: number;
  unassignedExtraCount: number;
  /** Játékosok, akik fuvarra várnak. */
  needsRideCount?: number;
  myAssignments: MyTravelAssignment[];
}

/** Hogyan utazik egy játékos (a backend PlayerTravelMode enumja). */
export enum PlayerTravelMode {
  None = 0,
  Direct = 1,
  NeedsRide = 2,
  Vehicle = 3,
}

export interface SetPlayerTravelRequest {
  mode: PlayerTravelMode;
  travelVehicleId?: number | null;
  travelStopId?: number | null;
  notes?: string | null;
}

export interface SaveMyRideRequest {
  seats: number;
  notes?: string | null;
  meetingPoint?: string | null;
  meetTime?: string | null;
}

export interface TravelCopyCandidate {
  eventId: number;
  eventTitle: string;
  eventType: EventType;
  eventStart: string;
  /** Van-e már a stáb által felvett jármű az utazásában. */
  hasVehicles: boolean;
  /** Ugyanazon a napon és időpontban van-e, mint a kiinduló esemény (jellemzően ugyanaz a sorozat). */
  sameSlot: boolean;
}

export interface CopyTravelResult {
  copied: number;
  skipped: number;
}

/** Egy játékos jelenlegi utazási módja az utazás alapján. */
export const playerTravelMode = (travel: EventTravel, playerId: number): PlayerTravelMode => {
  const p = travel.passengers.find((x) => x.playerId === playerId);
  if (!p) return PlayerTravelMode.None;
  if (p.goesDirectly) return PlayerTravelMode.Direct;
  return p.travelVehicleId ? PlayerTravelMode.Vehicle : PlayerTravelMode.NeedsRide;
};

/** Gyakori szerepek a plusz személyekhez (szabad szöveg is megadható). */
export const TRAVEL_EXTRA_ROLES = ['Szülő', 'Néző', 'Szertáros', 'Edző', 'Orvos', 'Gyúró', 'Vezető'];

/** "2026-10-04T07:30:00" -> "okt. 4. 7:30"; ha csak az idő számít, "7:30". */
export const formatTravelTime = (value?: string | null, withDate = false): string => {
  if (!value) return '';
  const date = new Date(value);
  return date.toLocaleString('hu-HU', {
    ...(withDate ? { month: 'short', day: 'numeric' } : {}),
    hour: '2-digit',
    minute: '2-digit',
  });
};
